import React, { useState, useCallback, useMemo } from "react";
import { styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { IoMdInformationCircleOutline } from "react-icons/io";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import styles from './HighRiskSkuTable.module.css';
import { useSimpleInfiniteScroll } from 'Samarth Module/pages/dailyPhasing/hooks/useSimpleInfiniteScroll';
import rightArrow from "../../../../assets/images/rightArrow.svg";
import downArrow from "../../../../assets/images/downArrow.svg";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "#2036C7",
        color: "#fff",
        textAlign: "center",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 400,
        border: "1px solid #EAECF0",

    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: 12,
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
    },
}));

const StyledTableRow = styled(TableRow)({});

const HighRiskSkuTable = ({ label, tableData, sortableColumns = [], columns = [], expandingRow }) => {
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    
    const handleSort = (col) => {
        if (!sortableColumns.includes(col)) return;

        if (sortColumn === col) {
            // Toggle direction if same column
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new column and default to ascending
            setSortColumn(col);
            setSortDirection('asc');
        };
    };

    const sortedData = useMemo(() => {
        if (!sortColumn) return tableData;

        // Separate parent and child rows
        const parentRows = tableData.filter(item => item.rowType?.toUpperCase() === 'PARENT');
        const childRows = tableData.filter(item => item.rowType?.toUpperCase() === 'CHILD');

        // Sort only parent rows
        const sortedParents = [...parentRows].sort((a, b) => {
            const aValue = a[sortColumn];
            const bValue = b[sortColumn];

            // Handle numeric comparison
            const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
            const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);

            if (sortDirection === 'asc') {
                return aNum - bNum;
            } else {
                return bNum - aNum;
            }
        });

        // Reconstruct the data with sorted parents and their linked children
        const result = [];
        sortedParents.forEach(parent => {
            // Add the parent row
            result.push(parent);
            
            // Find and add all child rows that belong to this parent
            const relatedChildren = childRows.filter(child => 
                child['CBU code'] === parent['CBU code']
            );
            result.push(...relatedChildren);
        });

        return result;
    }, [tableData, sortColumn, sortDirection]);

    // Use infinite scroll hook - loads 20 rows at a time
    const { data: displayData, containerRef } = useSimpleInfiniteScroll(sortedData, 20);

    const dataStyling = (col, value) => {
        if (label !== 'Overview' && ["Stock in hand", "CBU Description"].includes(col)) {
            return { color: '#1A2025 !important', fontWeight: '600 !important' };
        } else if (typeof value === 'number') {
            return { color: '#0066CC !important', fontWeight: '600 !important' };
        } else {
            return { color: '#1A2025 !important', fontWeight: '400 !important' };
        }
    };

    const splittingValues = (value) => {
        const values = value.split('|').map(v => v.trim());
        return (
            <>
                <span>
                    <span style={{ color: '#0066CC', fontWeight: 600 }}>{values[0]}</span>
                    <span style={{ color: '#1A2025', fontWeight: 400 }}> | </span>
                    <span style={{ color: '#1A2025', fontWeight: 600 }}>{values[1]}</span>
                </span>
            </>
        );
    };

    const handleExpandRow = (item) => {
        if (expandingRow) {
            expandingRow(item);
        }
    };

    return (
        <>
            <TableContainer className={styles.tableContainer} ref={containerRef}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((col, colIdx) => (
                                <StyledTableCell
                                    key={col}
                                    sx={{
                                        textAlign: "left",
                                        borderTopLeftRadius: colIdx === 0 ? '8px' : '0',
                                        borderTopRightRadius: colIdx === columns.length - 1 ? '8px' : '0',
                                        cursor: sortableColumns.includes(col) ? 'pointer' : 'default',
                                    }}
                                    onClick={() => handleSort(col)}
                                >
                                    <div className={styles.headers}>
                                        <span className={styles.headers}>{col}{col.trim() === 'Ideal | Open orders' && 
                                            <IoMdInformationCircleOutline style={{ cursor: 'pointer' }} />}
                                        </span>
                                        {sortableColumns.includes(col) && (
                                            <div className={styles.arrows}>
                                                {sortColumn === col ? (
                                                    sortDirection === 'asc' ? (
                                                        <ArrowDropUpIcon className={styles.arrow} />
                                                    ) : (
                                                        <ArrowDropDownIcon className={styles.arrow} />
                                                    )
                                                ) : (
                                                    <div className={styles.arrowsContainer}>
                                                        <ArrowDropUpIcon className={styles.arrow} style={{ marginBottom: '-8px' }} />
                                                        <ArrowDropDownIcon className={styles.arrow} />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </StyledTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayData.map((item, idx) => (
                            <React.Fragment key={idx}>
                                <StyledTableRow>
                                    {columns.map((col, colIdx) => (
                                        <StyledTableCell
                                            key={col}
                                            sx={{
                                                borderBottomLeftRadius: idx === displayData.length - 1 && colIdx === 0 ? '8px' : '0',
                                                borderBottomRightRadius: idx === displayData.length - 1 && colIdx === columns.length - 1 ? '8px' : '0',
                                                ...dataStyling(col, item[col])
                                            }}
                                        >
                                            {col === 'Ideal | Open orders' ? splittingValues(item[col]) :
                                                col === "Customer Name" && item['rowType'] === 'PARENT' ?
                                                    <span className={styles.customerName}>
                                                        {item['rowType'] === 'PARENT' &&
                                                            <img 
                                                                className={styles.expandIcon} 
                                                                src={item.isExpanded ? downArrow : rightArrow} 
                                                                alt='expand icon' 
                                                                onClick={() => handleExpandRow(item)} 
                                                            />
                                                        }
                                                        {item[col]}
                                                    </span>
                                                    : col === 'CBU Description' && item['rowType'] === "PARENT" ?
                                                        <div className={styles.cbuDesc}>
                                                            <span>{item['CBU Description']}</span>
                                                            <span className={styles.cbuCodeInHighSKU}>{item['CBU code']}</span>
                                                        </div> :
                                                        (col === 'Customer Name' && item['rowType'] === 'CHILD') ?
                                                    <div className={styles.cbuDesc}>
                                                        <span className={styles.expandedCustomerName}>{item['Customer Name']}</span>
                                                        <span className={styles.cbuCodeInHighSKU}>{`DC: ${item['DC']}`}</span>
                                                    </div> : item[col]
                                            }
                                        </StyledTableCell>
                                    ))}
                                </StyledTableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}

export default HighRiskSkuTable;